import { getSafeTracer, SpanStatusCode } from '../tracing/setup';

const TRACER_NAME = 'quartz-routing';
const SILO_ACCESSOR = Symbol.for('silo_v1');

interface NavLocation {
  pathname: string;
  search: string;
  hash: string;
  key?: string;
}

// Minimal history interface we depend on — matches quartz-routing's History type.
interface HistoryLike {
  location: { pathname: string };
  listen(listener: (location: NavLocation, action: string) => void): () => void;
}

type ScopedState = { values: Map<string, unknown> };
type SiloState = { BROWSING_CONTEXT?: ScopedState };

function getAppName(): string {
  const silo = (window as unknown as Record<symbol, SiloState>)[SILO_ACCESSOR];
  return (
    (silo?.BROWSING_CONTEXT?.values?.get('current_package_name') as
      | string
      | undefined) ?? ''
  );
}

function emitNavSpan(
  action: string,
  fromPath: string,
  historyKey: string,
  appName: string
): void {
  const tracer = getSafeTracer(TRACER_NAME);
  const span = tracer.startSpan(`navigation.${action}`, {
    attributes: {
      'navigation.type': action,
      'navigation.from_path': fromPath,
      'url.path': window.location.pathname,
      'url.query': window.location.search,
      'url.fragment': window.location.hash,
      'navigation.history_key': historyKey,
      'quartz.app_name': appName,
    },
  });
  span.setStatus({ code: SpanStatusCode.OK });
  span.end();
}

/**
 * Instruments SPA navigation by patching window.history.pushState /
 * replaceState and listening to popstate. No quartz app plugin needed —
 * call once after initTracing() (or pass instrumentSpaNavigation: true to
 * initTracing) and spans are emitted automatically for the page lifetime.
 * Returns an unlisten function for cleanup.
 */
export function instrumentSpaNavigation(): () => void {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if ((window.history.pushState as any).__otel_spa_nav_patched) {
    return () => {};
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const origPushState = (window.history.pushState as any).bind(window.history);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const origReplaceState = (window.history.replaceState as any).bind(
    window.history
  );

  let previousPath = window.location.pathname;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  window.history.pushState = function (state: any, title: string, url?: any) {
    origPushState(state, title, url);
    const appName = getAppName();
    const key = String(state?.key ?? '');
    emitNavSpan('push', previousPath, key, appName);
    previousPath = window.location.pathname;
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window.history.pushState as any).__otel_spa_nav_patched = true;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  window.history.replaceState = function (
    state: any,
    title: string,
    url?: any
  ) {
    origReplaceState(state, title, url);
    const appName = getAppName();
    const key = String(state?.key ?? '');
    emitNavSpan('replace', previousPath, key, appName);
    previousPath = window.location.pathname;
  };

  function onPopState(event: PopStateEvent): void {
    const appName = getAppName();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const key = String((event.state as any)?.key ?? '');
    emitNavSpan('pop', previousPath, key, appName);
    previousPath = window.location.pathname;
  }

  window.addEventListener('popstate', onPopState);

  return () => {
    window.history.pushState = origPushState;
    window.history.replaceState = origReplaceState;
    window.removeEventListener('popstate', onPopState);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    delete (window.history.pushState as any).__otel_spa_nav_patched;
  };
}

/**
 * Instruments quartz-routing navigation by attaching a history.listen() observer
 * that emits an instantaneous OTel span per SPA navigation.
 *
 * Call this from onAfterRender in a quartz app plugin, passing the history
 * instance from get(HISTORY_INSTANCE). Returns the unlisten function — store it
 * and call it in onUnmount to prevent duplicate listeners on hot reload.
 */
export function instrumentQuartzRouting(history: HistoryLike): () => void {
  let previousPath = history.location.pathname;

  return history.listen((location, action) => {
    const from = previousPath;
    previousPath = location.pathname;
    const appName = getAppName();
    const tracer = getSafeTracer(TRACER_NAME);
    const span = tracer.startSpan(`navigation.${action.toLowerCase()}`, {
      attributes: {
        'navigation.type': action.toLowerCase(),
        'navigation.from_path': from,
        'url.path': location.pathname,
        'url.query': location.search,
        'url.fragment': location.hash,
        'navigation.history_key': location.key ?? '',
        'quartz.app_name': appName,
      },
    });
    span.setStatus({ code: SpanStatusCode.OK });
    span.end();
  });
}
